import { Button, Flex, List, Text } from "@theorgchartdev/storybook";
import { DataSourceButton, DataSourceMenu } from "./DataSourceButton";
import { ApplyNewSourceAuthDefaults } from "./Utils";
import { GetSourceLabel } from "./DataSourceButton";
import { DataSourceConfigurationModal } from "./DataSources/DataSourceConfigurationModal";
import { ImportTypes } from "@/Utils/Constants";
import { ADPSORS } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/ADPSetupComposite";
import { Fragment, useRef, useState, useLayoutEffect } from "react";
import {
  CanAddAdditionalDataSource,
  LoadCurrentChartMapping
} from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/Utils";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import * as React from "react";
import cx from "classnames";
import { handleListItemClick } from "@/Utils/ComponentLib";
import { GetWSMgr } from "@/Utils/StateLib";
import {
  kEnterpriseConnectorList,
  kAppendConnectorList,
  kMergeConnectorList,
  kPhotoConnectorList,
  gAddSourcesLimitKey
} from "@coreapp/whitelabel/brand";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import {
  IsDataSourceSet,
  GetAppendDataType,
  upgradePillLabel,
  getAppendUpgradeText,
  getMergeUpgradeText
} from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";
import { PositionConfig } from "@coreapp/CoreApp/PositionConfig";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";
import { ChartDiagnostics } from "../ChartDiagnostics";
import UpgradePopover from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradePopover";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";

export const GetConnectorList = () => {
  return {
    [ImportTypes.import]: kEnterpriseConnectorList.sort((a, b) =>
      a.connector.localeCompare(b.connector)
    ),
    [ImportTypes.append]: kAppendConnectorList.sort((a, b) =>
      a.connector.localeCompare(b.connector)
    ),
    [ImportTypes.merge]: kMergeConnectorList.sort((a, b) => a.connector.localeCompare(b.connector)),
    [ImportTypes.photo]: kPhotoConnectorList.sort((a, b) => a.connector.localeCompare(b.connector))
  };
};

const GetConfigCopy = config => {
  return JSON.parse(JSON.stringify(config));
};

export const DataSourceSection = ({
  type,
  chartConfig,
  onChange,
  onSave,
  addDataTestId,
  sourceDataTestId,
  sourceOptionDataTestId,
  triggerProps
}) => {
  const [currentConfigOptions, setCurrentConfigOptions] = useState({ index: null, config: null });
  const [showUpgradeOnSave, setShowUpgradeOnSave] = useState(false);

  const [syncedConfigSnapshot, setSyncedConfigSnapshot] = useState(null);
  const { showPricingAndPackaging } = usePermissionCheck();

  useLayoutEffect(() => {
    if (!chartConfig) return;
    setSyncedConfigSnapshot(GetConfigCopy(chartConfig));
  }, [chartConfig]);

  const positionConfig = new PositionConfig(chartConfig);
  const chartDiagnostics = new ChartDiagnostics(chartConfig);

  const multiSourceMode =
    positionConfig.isPositionChart() &&
    positionConfig.isPositionDataIngestMultiple() &&
    type === ImportTypes.import;

  const numMaxAddSources = 8;

  const getBaseConfiguration = () => {
    return chartConfig[type];
  };

  const getPositionsConfiguration = () => {
    if (!multiSourceMode) return null;
    return chartConfig[type].positions || {};
  };

  const getHoldersConfiguration = () => {
    if (!multiSourceMode) return null;
    return chartConfig[type].holders || {};
  };

  const baseConfiguration = getBaseConfiguration();
  const positionsConfiguration = getPositionsConfiguration();
  const holdersConfiguration = getHoldersConfiguration();

  if (!baseConfiguration) return;

  const toggleConfigModal = (index, config, source = null) => {
    setCurrentConfigOptions({ index: index, config: config, source: source });
  };

  const openSourceConfigOptions = (index, config, source = null) => {
    toggleConfigModal(index, config, source);
  };

  const handleConfigUpdate = (updatedObj, idx, resetMainSource = false, source = null) => {
    if (multiSourceMode && source) {
      const configCopy = GetConfigCopy(
        source === "positions" ? positionsConfiguration : holdersConfiguration
      );

      if (idx != null) {
        if (configCopy[idx] == null) return;
        let currentConfigItem = configCopy[idx];
        configCopy[idx] = { ...currentConfigItem, ...updatedObj };
      } else {
        Object.assign(configCopy, updatedObj);
      }

      const newConfiguration = {
        [type]: {
          ...chartConfig[type],
          [source]: configCopy
        }
      };

      onChange(newConfiguration, [type]);
    } else {
      let configCopy = GetConfigCopy(baseConfiguration);

      if (idx != null) {
        if (configCopy[idx] == null) return;
        let currentConfigItem = configCopy[idx];
        configCopy[idx] = { ...currentConfigItem, ...updatedObj };
      } else {
        Object.assign(configCopy, updatedObj);
      }

      let updatedKeys = [type];
      let newConfiguration = { [type]: configCopy };

      if (resetMainSource) {
        let sourceKeys = ["headers", "fields", "sync", "Timestamp"];
        newConfiguration["sync"] = { schedule: "Off", diagnostics: { needRefresh: true } };
        newConfiguration["headers"] = null;
        newConfiguration["fields"] = null;
        newConfiguration["Timestamp"] = null;
        let updatedChartConfig = { ...chartConfig, ...newConfiguration };
        const { newChartConfig, needRFC } = LoadCurrentChartMapping(updatedChartConfig, type);
        if (needRFC === false) {
          newConfiguration["fields"] = newChartConfig["fields"];
        }
        updatedKeys = updatedKeys.concat(sourceKeys);
      }

      onChange(newConfiguration, updatedKeys);
    }

    toggleConfigModal(null, null);
  };

  const doResetIntegration = () => {
    const index = currentConfigOptions.index;
    const isAppendEntry = type === ImportTypes.append && index != null;

    let updatedKeys = isAppendEntry ? [type] : ["headers", "Timestamp"];
    let newConfiguration;

    if (multiSourceMode && currentConfigOptions.source) {
      const configCopy = GetConfigCopy(
        currentConfigOptions.source === "positions" ? positionsConfiguration : holdersConfiguration
      );

      newConfiguration = {
        [type]: {
          ...chartConfig[type],
          [currentConfigOptions.source]: configCopy
        }
      };
    } else {
      const configCopy = GetConfigCopy(baseConfiguration);
      if (isAppendEntry && configCopy[index]) {
        configCopy[index] = { ...configCopy[index], headers: null };
      }
      newConfiguration = { [type]: configCopy };
    }

    if (!isAppendEntry) {
      updatedKeys.forEach(key => {
        newConfiguration[key] = null;
      });
    }

    onChange(newConfiguration, updatedKeys);
    toggleConfigModal(null, null);

    if (onSave) {
      const additionalData = isAppendEntry
        ? { ...newConfiguration, resetConnectorState: { type: ImportTypes.append, idx: index } }
        : newConfiguration;
      onSave({ additionalData });
    }
  };

  const resetIntegration = () => {
    const wsMgr = GetWSMgr();
    wsMgr.CreateReactAlert(
      appstrp("Reset integration", "?"),
      appstr("Confirm reset integration"),
      () => doResetIntegration(),
      () => {},
      appstr("Reset integration"),
      appstr("Cancel")
    );
  };

  const changeDataSource = (updatedObj, index, source = null) => {
    let isMainSource = type === "import" && !multiSourceMode;
    let configCopy;

    if (multiSourceMode && source) {
      configCopy = GetConfigCopy(
        source === "positions" ? positionsConfiguration : holdersConfiguration
      );
    } else {
      configCopy = GetConfigCopy(baseConfiguration);
    }

    let existingConfig = isMainSource ? configCopy : configCopy[index];

    ApplyNewSourceAuthDefaults(updatedObj, existingConfig, type);

    if (existingConfig) {
      updatedObj["headers"] = null;
      updatedObj["mergeDupes"] = false;
      updatedObj["mergeByField"] = undefined;
      if (ADPSORS.includes(updatedObj.source)) {
        updatedObj["adpKeys"] = null;
        updatedObj["CustomADPFields"] = null;
      }
    }

    let currentConfig = { ...existingConfig, ...updatedObj };
    handleConfigUpdate(updatedObj, index, isMainSource, source);
    openSourceConfigOptions(index, currentConfig, source);
  };

  const handleCreateExtraSource = (updatedObj, sourceType = null) => {
    //check is done  in the getConnectorLimit, this alert will also need to be workshopped for new packages
    //const accountCanAddSource = CanAddAdditionalDataSource(chartConfig);
    //if (accountCanAddSource !== true) return;

    let configCopy;
    if (multiSourceMode && sourceType) {
      configCopy = GetConfigCopy(
        sourceType === "positions" ? positionsConfiguration : holdersConfiguration
      );
    } else {
      configCopy = GetConfigCopy(baseConfiguration);
    }

    let index = configCopy.length ?? 0;
    let existingConfig = configCopy[index] ?? {};
    ApplyNewSourceAuthDefaults(updatedObj, existingConfig, type);
    updatedObj._uid = index;
    configCopy.push(updatedObj);

    let newConfiguration;
    if (multiSourceMode && sourceType) {
      newConfiguration = {
        [type]: {
          ...chartConfig[type],
          [sourceType]: configCopy
        }
      };
    } else {
      newConfiguration = { [type]: configCopy };
    }

    onChange(newConfiguration, type);

    let currentConfig = { ...existingConfig, ...updatedObj };
    openSourceConfigOptions(index, currentConfig, sourceType);
  };

  const handleRemoveSource = (dataSourceIndex, sourceType = null) => {
    let configCopy;
    if (multiSourceMode && sourceType) {
      configCopy = GetConfigCopy(
        sourceType === "positions" ? positionsConfiguration : holdersConfiguration
      );
    } else {
      configCopy = GetConfigCopy(baseConfiguration);
    }

    configCopy.splice(dataSourceIndex, 1);

    let newConfiguration;
    if (multiSourceMode && sourceType) {
      newConfiguration = {
        [type]: {
          ...chartConfig[type],
          [sourceType]: configCopy
        }
      };
    } else {
      newConfiguration = { [type]: configCopy };
    }

    onChange(newConfiguration, type);
  };

  const onSingleClickSource = option => {};

  const onDoubleClickSource = option => {
    toggleConfigModal(option.index, option.value, option.sourceType);
  };

  const GetSourceOptions = (onClick, idx) => {
    let options = [];
    const connectorList = GetConnectorList();
    for (let sourceObj of connectorList[type]) {
      if (
        sourceObj.connector === "Append Table" &&
        !getFeatureAccess(enumFeaturesList.appendTables)
      )
        continue;
      let newOption = {};
      newOption.content = (
        <DataSourceButton
          chartConfig={chartConfig}
          dataTestId={sourceDataTestId?.(sourceObj.command)}
          dataSource={sourceObj.command}
          type={type}
          removeBackground={true}
          textProps={{ size: "xsmall" }}
        />
      );
      newOption.value = sourceObj.command;
      newOption.clickHandler = () => onClick({ source: sourceObj.command }, idx);
      options.push(newOption);
    }
    return options;
  };

  const getDataSourceFailure = (configIndex, sourceType) => {
    if (type === ImportTypes.import && !multiSourceMode) {
      return chartDiagnostics.getOverallDiagnostic();
    } else if (type === ImportTypes.append) {
      return chartDiagnostics.getDiagnosticByConfigIndex(type, configIndex);
    } else if (type === ImportTypes.merge) {
      return chartDiagnostics.getDiagnosticByConfigIndex(type, configIndex);
    }
    return null;
  };

  const BuildSourceOptions = (configuration, sourceType = null) => {
    return Object.entries(configuration).map(([index, config]) => {
      let configOption = {};
      configOption.label = GetSourceLabel(config["source"], type);
      configOption.value = config;
      configOption.index = index;
      configOption.sourceType = sourceType;
      configOption.content = (
        <Fragment key={index}>
          <ActionListItem
            contentBefore={
              <DataSourceControlComposite
                chartConfig={chartConfig}
                dataTestId={sourceOptionDataTestId?.(index)}
                config={config}
                sourceOptions={GetSourceOptions(
                  (updatedObj, idx) => changeDataSource(updatedObj, idx, sourceType),
                  index
                )}
                type={type}
                flexProps={{
                  direction: "row",
                  gap: "4px",
                  alignItems: "center"
                }}
                triggerProps={{
                  imageWidth: 48,
                  imageHeight: 48,
                  buttonWidth: 60,
                  removeBackground: true,
                  layout: "inline",
                  config: config,
                  handleOpenConfigModal: () => toggleConfigModal(index, config, sourceType)
                }}
                labelAfter={true}
              />
            }
            actions={[
              ...(getDataSourceFailure(parseInt(index), sourceType)
                ? [
                    {
                      dataTestId: sourceOptionDataTestId?.(`${index}-caution`),
                      iconName: "triangle-exclamation",
                      iconProps: { color: "warning" },
                      tooltipProps: {
                        message: chartDiagnostics.getFailureTooltipByConfigIndex(
                          type,
                          parseInt(index)
                        )
                      },
                      onClick: () => {},
                      checkActive: () => true
                    }
                  ]
                : []),
              {
                dataTestId: sourceOptionDataTestId?.(`${index}-edit`),
                iconName: "gear",
                onClick: () => toggleConfigModal(index, config, sourceType)
              },
              {
                dataTestId: sourceOptionDataTestId?.(`${index}-delete`),
                iconName: "trash-can",
                iconProps: { color: "error" },
                onClick: () => handleRemoveSource(index, sourceType)
              }
            ]}
          />
        </Fragment>
      );
      configOption.divider = true;
      configOption.clickHandler = handleListItemClick(onSingleClickSource, onDoubleClickSource);
      return configOption;
    });
  };

  const getConnectorLimit = () => {
    if (!baseConfiguration) return false;
    let numConnectors = chartConfig.append.length + chartConfig.merge.length;
    return numConnectors >= getFeatureAccess(enumFeaturesList.dataConnectorLimit);
  };

  const renderSingleSourceSection = (configuration, sourceType = null) => {
    const sourceSet = IsDataSourceSet(configuration?.["source"]);

    const changeSourceTrigger = (
      <Button
        isFullWidth={false}
        className={"oc-bottom-justify"}
        modifier={"secondary"}
        dataTestId={sourceType ? `change-data-source-${sourceType}` : "change-data-source"}
        {...triggerProps}
      >
        {sourceSet ? appstr("Change data source") : appstr("Choose data source")}
      </Button>
    );

    let usedSources = chartConfig.append.length + chartConfig.merge.length;
    let totalSources = getFeatureAccess(enumFeaturesList.dataConnectorLimit);
    let remainingSources = totalSources - usedSources;
    if (remainingSources < 0) remainingSources = 0;

    const areConfigsEqual = () => {
      if (!syncedConfigSnapshot) return false;

      // Deep compare using JSON stringify
      return JSON.stringify(syncedConfigSnapshot) === JSON.stringify(chartConfig);
    };

    const addSourceTrigger = (
      <Button
        dataTestId={addDataTestId}
        modifier={"secondary"}
        className={"oc-bottom-justify"}
        disabled={getConnectorLimit()}
        tooltipProps={
          remainingSources <= 0 && totalSources >= numMaxAddSources
            ? {
                message: appstr(gAddSourcesLimitKey),
                position: "right-end"
              }
            : null
        }
      >
        {appstr("Add data source")}
      </Button>
    );

    if (type === ImportTypes.import) {
      return (
        <Flex direction={"column"} gap={"8px"} flex={"1"} width={"100%"}>
          <ActionListItem
            actions={
              sourceSet
                ? [
                    ...(getDataSourceFailure(null, sourceType)
                      ? [
                          {
                            iconName: "triangle-exclamation",
                            iconProps: { size: "medium", color: "warning" },
                            dataTestId: "data-source-caution-btn",
                            tooltipProps: {
                              message: chartDiagnostics.getFailureTooltip(type, null)
                            },
                            onClick: () => {},
                            checkActive: () => true
                          }
                        ]
                      : []),
                    {
                      iconName: "gear",
                      iconProps: { size: "medium" },
                      dataTestId: "data-source-config-btn",
                      onClick: () => openSourceConfigOptions(null, configuration, sourceType)
                    }
                  ]
                : []
            }
            buttonSize={multiSourceMode ? "small" : "large"}
            contentBefore={
              <DataSourceControlComposite
                chartConfig={chartConfig}
                dataTestId={"data-source-config-trigger"}
                config={configuration}
                sourceOptions={GetSourceOptions((updatedObj, idx) =>
                  changeDataSource(updatedObj, idx, sourceType)
                )}
                type={type}
                flexProps={{
                  direction: "row",
                  gap: "4px",
                  alignItems: "center"
                }}
                triggerProps={{
                  imageWidth: multiSourceMode ? 48 : 72,
                  imageHeight: multiSourceMode ? 48 : 72,
                  buttonWidth: multiSourceMode ? 60 : 84,
                  removeBackground: true,
                  layout: "inline",
                  buttonPadding: 8,
                  labelProps: { size: multiSourceMode ? "medium" : "large" },
                  config: configuration,
                  hideQuickAdd: !sourceSet
                }}
                labelAfter={true}
              />
            }
          />
          <Flex alignItems={"flex-start"} className={"oc-bottom-justify"}>
            <DataSourceControlComposite
              chartConfig={chartConfig}
              config={configuration}
              sourceOptions={GetSourceOptions((updatedObj, idx) =>
                changeDataSource(updatedObj, idx, sourceType)
              )}
              type={type}
              popoverProps={{
                trigger: changeSourceTrigger,
                triggerProps: {
                  ...triggerProps,
                  id: sourceType ? `change-source-btn-${sourceType}` : "change-source-btn"
                }
              }}
            />
          </Flex>
        </Flex>
      );
    } else {
      return (
        <Flex direction={"column"} gap={"8px"} flex={"1"} width={"100%"} overflow={"auto"}>
          <div className={cx("oc-list-container", "ocn-list-scrollbar")}>
            <List
              enableVirtualization={false}
              listWidth="fill"
              options={BuildSourceOptions(configuration, sourceType)}
              coloredSelection={true}
            />
          </div>
          <Flex alignItems={"flex-start"} className={"oc-bottom-justify"}>
            {(!areConfigsEqual() ||
              !getConnectorLimit() ||
              totalSources >= numMaxAddSources ||
              !showPricingAndPackaging) && (
              <DataSourceControlComposite
                chartConfig={chartConfig}
                config={configuration}
                sourceOptions={GetSourceOptions(updatedObj =>
                  handleCreateExtraSource(updatedObj, sourceType)
                )}
                type={type}
                popoverProps={{ trigger: addSourceTrigger }}
              />
            )}
            {showPricingAndPackaging &&
              getConnectorLimit() &&
              areConfigsEqual() &&
              totalSources < numMaxAddSources && (
                <UpgradePopover
                  pillLabel={upgradePillLabel}
                  pillTestId={
                    type === ImportTypes.append
                      ? "data-sources-append-upgrade-pill"
                      : "data-sources-merge-upgrade-pill"
                  }
                  popoverText={
                    type === ImportTypes.append ? getAppendUpgradeText() : getMergeUpgradeText()
                  }
                  featureKey={enumFeaturesList.dataConnectorLimit}
                />
              )}
          </Flex>
        </Flex>
      );
    }
  };

  const renderSourceSections = () => {
    if (multiSourceMode) {
      return (
        <Flex
          direction={"column"}
          gap={"16px"}
          flex={"1"}
          width={"100%"}
          className={cx("oc-list-container", "ocn-list-scrollbar")}
          overflow={"auto"}
        >
          <Flex direction="column" gap="8px" width="100%">
            <Flex direction="row" alignItems="center" gap="8px">
              <Text modifier={"bold"} size={"small"} colorModifier={"grey"}>
                {appstr("Employee dataset")}
              </Text>
            </Flex>
            {renderSingleSourceSection(holdersConfiguration, "holders")}
          </Flex>
          <Flex direction="column" gap="8px" width="100%">
            <Flex direction="row" alignItems="center" gap="8px">
              <Text modifier={"bold"} size={"small"} colorModifier={"grey"}>
                {appstr("Position dataset")}
              </Text>
            </Flex>
            {renderSingleSourceSection(positionsConfiguration, "positions")}
          </Flex>
        </Flex>
      );
    }

    if (
      positionConfig.isPositionChart() &&
      (type === ImportTypes.append || type === ImportTypes.merge)
    ) {
      const isSplitByDataset = Object.values(baseConfiguration).some(
        config => config?.dataType === "employees" || config?.dataType === "positions"
      );

      if (!isSplitByDataset) {
        return renderSingleSourceSection(baseConfiguration);
      }

      const employeeDataSources = Object.entries(baseConfiguration)
        .filter(([index, config]) => {
          return GetAppendDataType(config) === "employees";
        })
        .reduce((acc, [index, config]) => {
          acc[index] = config;
          return acc;
        }, {});

      const positionDataSources = Object.entries(baseConfiguration)
        .filter(([index, config]) => {
          return GetAppendDataType(config) === "positions";
        })
        .reduce((acc, [index, config]) => {
          acc[index] = config;
          return acc;
        }, {});

      const hasEmployeeData = Object.keys(employeeDataSources).length > 0;
      const hasPositionData = Object.keys(positionDataSources).length > 0;

      return (
        <Flex
          direction={"column"}
          gap={"16px"}
          flex={"1"}
          width={"100%"}
          className={cx("oc-list-container", "ocn-list-scrollbar")}
          overflow={"auto"}
        >
          {hasEmployeeData && (
            <Flex direction="column" gap="8px" width="100%" flex={"1"}>
              <Flex direction="row" alignItems="center" gap="8px">
                <Text modifier={"bold"} size={"small"} colorModifier={"grey"}>
                  {appstr("Employee dataset")}
                </Text>
              </Flex>
              {renderSingleSourceSection(employeeDataSources, "employees")}
            </Flex>
          )}
          {hasPositionData && (
            <Flex direction="column" gap="8px" width="100%" flex={"1"}>
              <Flex direction="row" alignItems="center" gap="8px">
                <Text modifier={"bold"} size={"small"} colorModifier={"grey"}>
                  {appstr("Position dataset")}
                </Text>
              </Flex>
              {renderSingleSourceSection(positionDataSources, "positions")}
            </Flex>
          )}
        </Flex>
      );
    }

    return renderSingleSourceSection(baseConfiguration);
  };

  return (
    <Fragment>
      <Flex direction={"column"} gap={"8px"} height={"100%"}>
        {renderSourceSections()}
      </Flex>

      {currentConfigOptions.config != null && (
        <DataSourceConfigurationModal
          config={currentConfigOptions.config}
          handleUpdate={(updatedObj, idx, resetMainSource) =>
            handleConfigUpdate(updatedObj, idx, resetMainSource, currentConfigOptions.source)
          }
          resetIntegration={resetIntegration}
          idx={currentConfigOptions.index}
          show={true}
          chartConfig={chartConfig}
          type={type}
          onClose={() => toggleConfigModal(null, null)}
        />
      )}
    </Fragment>
  );
};

const DataSourceControlComposite = ({
  config,
  chartConfig,
  sourceOptions,
  type,
  popoverProps,
  triggerProps,
  dataTestId,
  flexProps
}) => {
  const openCtrlRef = useRef(null);
  return (
    <DataSourceMenu
      chartConfig={chartConfig}
      dataTestId={dataTestId}
      dataSource={config["source"]}
      type={type}
      options={sourceOptions}
      openCtrlRef={openCtrlRef}
      triggerProps={triggerProps}
      popoverProps={popoverProps}
      flexProps={flexProps}
    />
  );
};
